import { Component, type ErrorInfo, type ReactNode } from "react";
import { siteConfig } from "@/lib/site-config";

interface Props {
  children: ReactNode;
}
interface State {
  hasError: boolean;
}

const RELOAD_KEY = "cx-chunk-reload-ts";

/**
 * Catches render errors from lazily-loaded route chunks. After a new deploy an
 * old tab can hold a stale chunk hash; importing it throws and would otherwise
 * leave an empty #root. We reload once (with a cooldown so we never loop) to
 * pick up the fresh chunk, and show a usable fallback otherwise.
 */
class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    const msg = error?.message || "";
    const isChunkError = /loading( css)? chunk|dynamically imported module|failed to fetch|importing a module/i.test(
      msg
    );
    if (isChunkError && typeof window !== "undefined") {
      const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
      if (Date.now() - last > 10000) {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: "60vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "1rem",
            padding: "2rem",
            textAlign: "center",
          }}
        >
          <h1 style={{ fontSize: "1.5rem", fontWeight: 700 }}>Something went wrong</h1>
          <p style={{ maxWidth: "32rem" }}>
            Please refresh the page. If the problem continues, reach us at{" "}
            <a href={siteConfig.phoneHref} style={{ fontWeight: 600, textDecoration: "underline" }}>
              {siteConfig.phone}
            </a>
            .
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: "0.6rem 1.25rem",
              borderRadius: "0.75rem",
              fontWeight: 600,
              background: "hsl(202 68% 30%)",
              color: "#fff",
              border: "none",
              cursor: "pointer",
            }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
